/**
 * @module shared/hooks/useUserScope
 *
 * Central hook for all "who can see what" scope restrictions derived from the
 * authenticated user's session.
 *
 * The session supplies three types of scope constraints:
 *   - `companyIds`        — the provider companies the user is allowed to access
 *   - `fundingSourceIds`  — the funding sources the user is allowed to access
 *   - `subcontractor`     — a legacy session flag (`subcontractor === 2`) that restricts a user to funding sources even if the specific ID list is omitted
 *
 * Any list hook or pagination hook that fetches data scoped to a provider or
 * funding source should consume this hook rather than reading `useAuth` directly.
 * This ensures the scope logic is defined in exactly one place and evolves
 * consistently across the entire application.
 *
 * @example
 * ```ts
 * const { applyProviderScope, applyFundingSourceScope } = useUserScope();
 *
 * // In a vehicle list query:
 * const scopedQuery = applyProviderScope(baseQuery, userSelectedCompanyIds);
 *
 * // In a funding source dropdown query:
 * const fsBaseQuery = applyFundingSourceScope([['equal', 'major', true]]);
 * ```
 */

import { useMemo } from 'react';
import { useAuth } from '@/shared/providers/AuthProvider';
import { SUBCONTRACTOR_TYPE } from '@/shared/types/auth.types';

/**
 * Session `user.subcontractor` values that are NOT treated as "subcontractor"
 * by the legacy Rails ability layer. Mirrors `NON_SUBCONTRACTOR_TYPES` in
 * drt_ui app/lib/auth/drt_ability.rb — both the standard normal user (0) and
 * an Ecolane internal account (4) bypass the subcontractor-only restrictions
 * (e.g. `category.subcontractor = true` in feedback / driver_incident /
 * roadcall queries).
 */
const NON_SUBCONTRACTOR_TYPES: readonly number[] = [
  SUBCONTRACTOR_TYPE.NORMAL,
  SUBCONTRACTOR_TYPE.ECOLANE,
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface UserScopeResult {
  // --- Provider (company) scope ---
  /** Numeric IDs of the providers the current user may access. Empty = no restriction. */
  allowedProviderIds: number[];
  /** `true` when the user is restricted to a specific set of providers. */
  isProviderRestricted: boolean;
  /**
   * Returns a new DSL query array with a `["in","company.id",…]` clause appended
   * when the user is provider-restricted.
   *
   * If `userFilter` is also provided (IDs chosen by the user in a picker) the
   * **intersection** of the two sets is used so the user can narrow further but
   * never widen beyond their allowed scope.
   *
   * When no restriction applies and no `userFilter` is provided the original
   * `query` is returned unchanged.
   */
  applyProviderScope: (query: unknown[][], userFilter?: number[]) => unknown[][];
  /**
   * Intersects a user-provided list of provider string IDs with the session's allowed list.
   * Returns `['-1']` if the intersection is empty so queries return zero results.
   * If the user is unrestricted, returns the input list unchanged.
   * If the input list is empty, returns all allowed IDs.
   */
  intersectAllowedProviderIds: (userIds: string[]) => string[];
  providerScopeCacheKey: string;

  // --- Funding source scope ---
  /** Numeric IDs of the funding sources the current user may access. Empty = no restriction. */
  allowedFundingSourceIds: number[];
  /** `true` when the user is restricted to a specific set of funding sources. */
  isFundingSourceRestricted: boolean;
  /**
   * Returns a new DSL query array with a `["in","id",…]` clause appended
   * when the user is funding-source-restricted.
   *
   * Follows the same intersection logic as `applyProviderScope`.
   */
  applyFundingSourceScope: (query: unknown[][], userFilter?: number[]) => unknown[][];
  /**
   * Intersects a user-provided list of funding source string IDs with the session's allowed list.
   * Returns `['-1']` if the intersection is empty.
   */
  intersectAllowedFundingSourceIds: (userIds: string[]) => string[];
  fundingSourceScopeCacheKey: string;

  // --- Subcontractor flag ---
  /**
   * `true` when the session user is a subcontractor of any flavour
   * (company-restricted, funding-source-restricted, or client-pool). Mirrors
   * Rails `ability.subcontractor?` (drt_ui app/models/user.rb), which is
   * `user.subcontractor != USER_SUBCONTRACTOR_NORMAL` after the Ecolane→Normal
   * normalisation also applied here via {@link NON_SUBCONTRACTOR_TYPES}.
   *
   * Consumers use this to add the `[:equal, 'category.subcontractor', true]`
   * clause that Rails appends to feedback / driver_incident / roadcall
   * authorization queries — see Rails `Query::FeedbackQuery#model_authorization_search_query_expr`.
   */
  isSubcontractor: boolean;
  /**
   * The current user's login name from the session, or `undefined` if not yet loaded.
   * Used by `useUserEvents` to scope the query to the current user's own events
   * (`[:equal, :agent, ability.user_login]`) when `isSubcontractor` is true.
   */
  userLogin: string | undefined;
}

// ---------------------------------------------------------------------------
// Helper
// ---------------------------------------------------------------------------

/**
 * Builds a scoped query clause. Pure function — no React dependencies.
 *
 * @param baseQuery     Existing DSL query array to extend.
 * @param allowedIds    IDs from the user's session (empty = no restriction).
 * @param field         The DSL field to filter on, e.g. `"company.id"` or `"id"`.
 * @param userFilter    Optional IDs chosen by the user in a UI picker.
 */
function buildScopeClause(
  baseQuery: unknown[][],
  allowedIds: number[],
  field: string,
  userFilter?: number[],
): unknown[][] {
  if (allowedIds.length === 0) {
    // No restriction — honour the user filter as-is if provided.
    if (userFilter && userFilter.length > 0) {
      return [...baseQuery, ['in', field, userFilter]];
    }
    return baseQuery;
  }

  // Provider/funding-source restricted user.
  if (userFilter && userFilter.length > 0) {
    // Intersect allowed IDs with user-chosen filter.
    const intersection = userFilter.filter((id) => allowedIds.includes(id));
    // [-1] sentinel: intersection is empty → return zero rows, not everything.
    return [...baseQuery, ['in', field, intersection.length > 0 ? intersection : [-1]]];
  }

  // No explicit user filter — scope to all allowed IDs.
  return [...baseQuery, ['in', field, allowedIds]];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useUserScope(): UserScopeResult {
  const { user } = useAuth();

  // --- Provider IDs ---
  const allowedProviderIds = useMemo<number[]>(
    () =>
      user?.companyIds?.length
        ? user.companyIds.map(Number).filter((id) => !Number.isNaN(id))
        : [],
    [user?.companyIds],
  );
  const isProviderRestricted = allowedProviderIds.length > 0;

  const applyProviderScope = useMemo(
    () =>
      (query: unknown[][], userFilter?: number[]): unknown[][] =>
        buildScopeClause(query, allowedProviderIds, 'company.id', userFilter),
    [allowedProviderIds],
  );

  const providerScopeCacheKey = useMemo(
    () => (allowedProviderIds.length > 0 ? [...allowedProviderIds].sort((a, b) => a - b).join(',') : ''),
    [allowedProviderIds]
  );

  const intersectAllowedProviderIds = useMemo(
    () =>
      (userIds: string[]): string[] => {
        if (!isProviderRestricted) return userIds;
        if (userIds.length === 0) return allowedProviderIds.map(String);
        const allowedStrings = allowedProviderIds.map(String);
        const inter = userIds.filter((id) => allowedStrings.includes(id));
        return inter.length > 0 ? inter : ['-1'];
      },
    [isProviderRestricted, allowedProviderIds]
  );

  // --- Funding source IDs ---
  const allowedFundingSourceIds = useMemo<number[]>(
    () =>
      user?.fundingSourceIds?.length
        ? user.fundingSourceIds.map(Number).filter((id) => !Number.isNaN(id))
        : [],
    [user?.fundingSourceIds],
  );
  const isFundingSourceRestricted = allowedFundingSourceIds.length > 0 || user?.subcontractor === 2;

  const applyFundingSourceScope = useMemo(
    () =>
      (query: unknown[][], userFilter?: number[]): unknown[][] =>
        buildScopeClause(query, allowedFundingSourceIds, 'id', userFilter),
    [allowedFundingSourceIds],
  );

  const fundingSourceScopeCacheKey = useMemo(
    () => (allowedFundingSourceIds.length > 0 ? [...allowedFundingSourceIds].sort((a, b) => a - b).join(',') : ''),
    [allowedFundingSourceIds]
  );

  const intersectAllowedFundingSourceIds = useMemo(
    () =>
      (userIds: string[]): string[] => {
        if (!isFundingSourceRestricted) return userIds;
        if (userIds.length === 0) return allowedFundingSourceIds.map(String);
        const allowedStrings = allowedFundingSourceIds.map(String);
        const inter = userIds.filter((id) => allowedStrings.includes(id));
        return inter.length > 0 ? inter : ['-1'];
      },
    [isFundingSourceRestricted, allowedFundingSourceIds]
  );

  // --- Subcontractor flag ---
  // Matches Rails `ability.subcontractor?` semantics: any non-normal,
  // non-Ecolane `user.subcontractor` value flags the user as a subcontractor.
  // `null`/`undefined` (no value in the session) is treated as Normal.
  const isSubcontractor =
    user?.subcontractor != null && !NON_SUBCONTRACTOR_TYPES.includes(user.subcontractor);

  // --- User login ---
  // Exposed so consumers (e.g. useUserEvents) can scope queries by agent login
  // without needing a separate useAuth import.
  const userLogin = user?.login;

  return {
    allowedProviderIds,
    isProviderRestricted,
    applyProviderScope,
    intersectAllowedProviderIds,
    providerScopeCacheKey,
    allowedFundingSourceIds,
    isFundingSourceRestricted,
    applyFundingSourceScope,
    intersectAllowedFundingSourceIds,
    fundingSourceScopeCacheKey,
    isSubcontractor,
    userLogin,
  };
}
